import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectActiveLinkedService,
  selectLinkedServices,
  selectLinkedServicesStatus,
  activeLinkedServiceChanged,
} from 'src/v3/manage/manage.slice'

/**
 * Hook to manage linked service selection across V4 manage pages.
 *
 * This hook handles:
 * 1. Restoring linked service from router location state (when navigating back)
 * 2. Auto-selecting the first service if none is selected
 * 3. Maintaining consistent linked service selection in Redux
 *
 * @example
 * ```tsx
 * export default function GroupsV4(): ReactElement {
 *   let linkedServiceID = useLinkedServiceSelection()
 *
 *   // linkedServiceID will be auto-selected or restored from navigation
 *   return <div>...</div>
 * }
 * ```
 */
export function useLinkedServiceSelection(): string {
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()

  let linkedServiceID = useAppSelector(selectActiveLinkedService)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  useEffect(() => {
    let state = location.state as { linkedServiceID?: string } | null

    // If navigating back with a linkedServiceID in state, use that
    if (state?.linkedServiceID) {
      dispatch(activeLinkedServiceChanged(state.linkedServiceID))
      // Clear the state after using it
      navigate(location.pathname, {
        replace: true,
        state: { ...state, linkedServiceID: undefined },
      })
    } else if (
      (!linkedServiceID || linkedServiceID === 'unselected') &&
      linkedServicesStatus === 'fulfilled' &&
      linkedServices.length > 0
    ) {
      // Otherwise auto-select first service if none selected
      dispatch(activeLinkedServiceChanged(linkedServices[0].id))
    }
  }, [
    linkedServiceID,
    linkedServicesStatus,
    linkedServices,
    location.state,
    location.pathname,
    navigate,
    dispatch,
  ])

  return linkedServiceID
}
